<template>
  <div ref="swiperRef" class="xy-swiper">
    <swiper
      :key="refreshIndex"
      class="xy-swiper-content"
      :modules="modulesList"
      :direction="props.direction"
      :dir="props.rtl"
      :pagination="paginationConfig"
      :navigation="showNavigation"
      :autoplay="autoplayConfig"
      :loop="slideItems.length > 3 && props.loop"      
      :effect="props.effect"
      :initial-slide="slideItems.length > 1 ? props.initialSlide < slideItems.length -1 ? props.initialSlide : 0 : 0"
      :slides-per-view="slideItems.length > 1 ? props.slidesPerView : 1"
      :centered-slides="props.centeredSlides"
      :space-between="slideItems.length > 1 ? props.spaceBetween : 0"
      :coverflowEffect="coverflowEffectConfig"
      :mousewheel="{ sensitivity: 0.8, thresholdDelta: 10, thresholdTime: 800 }"
      :touchRatio="props.touchRatio"                
      :touchAngle="45"                 
      :shortSwipes="true"              
      :longSwipesRatio="0.3"          
      :resistance="false"              
      :followFinger="true"            
      @swiper="onSwiper"
      @slideChange="onSlideChange"
    >
      <swiper-slide v-for="(item, index) in slideItems" :key="index">
        <xy-image v-if="item.coverType === 1 && item.cover" :src="item.cover" :fit="item.fit"></xy-image>
        <xy-svga v-else-if="item.coverType === 2 && item.cover" :src="item.cover" :contentMode="item.fit"></xy-svga>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script setup>
// https://swiperjs.com/swiper-api
import { computed } from 'vue'
import { info, defaultProps } from './config'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Navigation, Autoplay, EffectFade, EffectCoverflow, Mousewheel, A11y } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/effect-fade'
import 'swiper/css/effect-coverflow'
import XyImage from '@/packages/components/base/image'
import XySvga from '@/packages/components/base/svga'
import { useSwiper } from '@/packages/components/hook/useSwiper'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)

const paginationConfig = computed(() => {
  const config = {
    enabled: props.showPagination,
    clickable: true,
    type: props.paginationType
  }
  if (props.paginationPosition === 'top') {
    config.horizontalClass = 'swiper-pagination-top'
  } else if (['left', 'right'].includes(props.paginationPosition)) {
    config.verticalClass = `swiper-pagination-${props.paginationPosition}`
  } else { 
    config.horizontalClass = 'swiper-pagination-bottom'
  }
  return config
})

const autoplayConfig = computed(() => ({
  enabled: props.autoplay,
  delay: props.delay,
  stopOnLastSlide: props.stopOnLastSlide,
  disableOnInteraction: false
}))

const coverflowEffectConfig = computed(() => ({
  depth: props.coverflowDepth,
  modifier: props.coverflowModifier,
  rotate: props.coverflowRotate,
  scale: props.coverflowScale,
  slideShadows: props.coverflowShadow,
  stretch: props.coverflowStretch
}))

// 测试发现老款vivo手机自带浏览器使用Pagination时不兼容
// UA: mozilla/5.0 (linux; android 7.1.2; vivo y79a build/n2g47h; wv) applewebkit/537.36 (khtml, like gecko) chrome/62.0.3202.84 mobile safari/537.36 vivobrowser/7.6.10.2
const isLowVivoBrowser = computed(() => {
  if (navigator.userAgent.toLowerCase() && navigator.userAgent.toLowerCase().indexOf('vivobrowser') > -1) {
    const match = navigator.userAgent.toLowerCase().match(/vivobrowser\/([\d.]+)/)
    if (match) {
      const majorVersion = parseInt(match[1].split('.')[0])
      return majorVersion < 10
    }
    return false
  }
  return false
})

const modulesList = computed(() => {
  const list = []
  if (props.showPagination && !isLowVivoBrowser.value) list.push(Pagination)
  if (props.showNavigation) list.push(Navigation)
  if (props.autoplay) list.push(Autoplay)
  if (props.mousewheel) list.push(Mousewheel)
  if (props.a11y) list.push(A11y)
  
  switch (props.effect) {
    case 'fade':
      list.push(EffectFade)
      break
    case 'coverflow':
      list.push(EffectCoverflow)
      break
  }
  return list
})

const {
  swiperRef,
  swiperInstance,
  refreshIndex,
  slideItems,
  onSwiper,
  onSlideChange,
  slidePrev,
  slideNext,
  slideTo
} = useSwiper(props)

const fractionSize = computed(() => {
  return props.paginationBulletSize * 1.6  // 字体大小设置为圆点大小的1.5倍
})

defineExpose({
  swiperInstance,
  onSwiper,
  onSlideChange,
  slidePrev,
  slideNext,
  slideTo
})

</script>
<style lang="scss" scoped>
.xy-swiper {
  width: 100%;
  height: 100%;
  position: relative;
}

.xy-swiper-content {
  width: 100%;
  height: 100%;
  z-index: unset !important;

  :deep(.swiper-wrapper){
    z-index: unset !important;
  }

  :deep(.swiper-pagination) {
    --swiper-pagination-bullet-size:  v-bind('props.paginationBulletSize + "px"');
    --swiper-pagination-fraction-color: v-bind('props.paginationColor');
    --swiper-pagination-color: v-bind('props.paginationColor');
    --swiper-pagination-bullet-inactive-color: v-bind('props.paginationInactiveColor');
    --swiper-pagination-bullet-inactive-opacity: v-bind('props.paginationInactiveOpacity');

    &.swiper-pagination-fraction {
      font-size: v-bind('fractionSize');
    }

    &.swiper-pagination-current {
      font-weight: bold;
      color: v-bind('props.paginationColor');
    }

    &.swiper-pagination-total {
      color: v-bind('props.paginationInactiveColor');
    }

    &.swiper-pagination-bottom {
      top: auto;
      bottom: 15px;
    }

    &.swiper-pagination-top {
      top: 15px;
      bottom: auto;
    }

    &.swiper-pagination-left {
      left: 18px;
      right: auto;
    }

    &.swiper-pagination-right {
      left: auto;
      right: 18px;
    }
  }
}
</style>
